import { useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

const MIN_HEIGHT = 128;

interface DynamicHeightIFrameProps {
  /** Accessible title for the iframe. */
  title: string;
  /** HTML markup to render; sanitized before display. */
  children: string;
}

/**
 * Renders sanitized HTML in a sandboxed iframe that grows to fit its content.
 * @see https://stackoverflow.com/questions/67218249
 */
export default function DynamicHeightIFrame({
  title,
  children,
}: DynamicHeightIFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(MIN_HEIGHT);

  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(children), [children]);

  // Fires on initial load and whenever `srcDoc` changes (which reloads the frame).
  const fitToContent = () => {
    const body = frameRef.current?.contentWindow?.document?.body;
    if (body) setHeight(Math.max(body.scrollHeight, MIN_HEIGHT));
  };

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={sanitizedHtml}
      sandbox='allow-same-origin'
      onLoad={fitToContent}
      className='eye:block eye:w-full eye:border-0 eye:bg-transparent'
      style={{ height }}
    />
  );
}
